import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Store, Wallet, ShieldCheck, ListChecks } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import {
  topupWallet,
  getArchitect,
  lockArchitect,
  getFreelancer,
  lockFreelancer,
  getSkills,
} from "../../services/zumaleAdminMarketplace";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

function WalletTopup() {
  const [architectId, setArchitectId] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!architectId || !amount) {
      toast.error("Architect ID and amount are required.");
      return;
    }
    setBusy(true);
    try {
      const w = await topupWallet(Number(architectId), {
        amount: Number(amount),
        reference: reference || null,
        proofUrl: proofUrl || null,
      });
      setResult(w);
      toast.success(`Wallet credited. New balance ${fmtINR(w.balance)}.`);
      setAmount("");
      setReference("");
      setProofUrl("");
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Top-up failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-3 rounded-lg border border-gray-200 bg-white p-6">
      <p className="text-sm text-gray-500">Record an offline wallet top-up for an architect (Party A).</p>
      <input className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Architect ID"
        value={architectId} onChange={(e) => setArchitectId(e.target.value)} />
      <input className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Amount (₹)" type="number"
        value={amount} onChange={(e) => setAmount(e.target.value)} />
      <input className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Payment reference (UTR/txn)"
        value={reference} onChange={(e) => setReference(e.target.value)} />
      <input className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Proof URL (optional)"
        value={proofUrl} onChange={(e) => setProofUrl(e.target.value)} />
      <button onClick={submit} disabled={busy}
        className="rounded-md bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50">
        {busy ? "Crediting…" : "Credit Wallet"}
      </button>
      {result && (
        <p className="text-sm text-emerald-700">New balance for architect {result.architectId}: {fmtINR(result.balance)}</p>
      )}
    </div>
  );
}

function Moderation() {
  const [kind, setKind] = useState("architect");
  const [id, setId] = useState("");
  const [entity, setEntity] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const data = kind === "architect" ? await getArchitect(Number(id)) : await getFreelancer(Number(id));
      setEntity(data);
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Not found.");
      setEntity(null);
    } finally {
      setBusy(false);
    }
  };

  const setLock = async (lock) => {
    setBusy(true);
    try {
      const fn = kind === "architect" ? lockArchitect : lockFreelancer;
      const updated = await fn(Number(id), lock, lock ? reason : null);
      setEntity(updated);
      toast.success(lock ? "Account locked." : "Account unlocked.");
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-3 rounded-lg border border-gray-200 bg-white p-6">
      <div className="flex gap-2">
        <select value={kind} onChange={(e) => { setKind(e.target.value); setEntity(null); }}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm">
          <option value="architect">Architect</option>
          <option value="freelancer">Freelancer</option>
        </select>
        <input className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder={`${kind} ID`}
          value={id} onChange={(e) => setId(e.target.value)} />
        <button onClick={load} disabled={busy}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Load
        </button>
      </div>
      {entity && (
        <div className="rounded-md border border-gray-100 p-3 text-sm">
          <div className="font-semibold text-gray-800">{entity.name}</div>
          <div className="text-gray-500">{entity.email} · {entity.mobile}</div>
          <div className="mt-1">Status:{" "}
            <span className={entity.isLocked ? "text-rose-600" : "text-emerald-600"}>
              {entity.isLocked ? "Locked" : "Active"}
            </span>
          </div>
          {!entity.isLocked && (
            <input className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Reason for locking"
              value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          <button onClick={() => setLock(!entity.isLocked)} disabled={busy}
            className={`mt-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              entity.isLocked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            }`}>
            {entity.isLocked ? "Unlock Account" : "Lock Account"}
          </button>
        </div>
      )}
    </div>
  );
}

function SkillCatalog() {
  const [skills, setSkills] = useState([]);
  useEffect(() => {
    getSkills().then((s) => setSkills(Array.isArray(s) ? s : [])).catch(() => {});
  }, []);
  const groups = { SOFTWARE: [], ARCHITECTURE: [] };
  skills.forEach((s) => groups[s.family]?.push(s));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {["SOFTWARE", "ARCHITECTURE"].map((f) => (
        <div key={f} className="rounded-lg border border-gray-200 bg-white p-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">{f}</h4>
          <ul className="space-y-1 text-sm text-gray-700">
            {groups[f].map((s) => (
              <li key={s.skillId} className="flex justify-between">
                <span>{s.name}</span>
                <span className={s.isActive ? "text-emerald-600 text-xs" : "text-gray-400 text-xs"}>
                  {s.isActive ? "active" : "inactive"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: "topup", label: "Wallet Top-up", icon: Wallet },
  { key: "moderation", label: "Moderation", icon: ShieldCheck },
  { key: "skills", label: "Skill Catalog", icon: ListChecks },
];

function MarketplaceAdmin() {
  const [active, setActive] = useState("topup");
  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Work Marketplace" subtitle="Architect ↔ Freelancer administration" icon={Store} />
      <Tabs tabs={TABS.map((t) => ({ key: t.key, label: t.label }))} active={active} onChange={setActive} />
      {active === "topup" && <WalletTopup />}
      {active === "moderation" && <Moderation />}
      {active === "skills" && <SkillCatalog />}
    </div>
  );
}

export default MarketplaceAdmin;
